import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useCMSettings, useUpdateCMSettings } from '@/hooks/useChangeManagements';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Org-wide change-management settings, on Organization Admin → Change management
 * beside the approval policies. Currently: the gate for scheduled auto-start (off by default — auto-executing
 * network changes unattended is opt-in at the org level, then per-CM).
 */
export default function OrgChangeManagementSection() {
  const { data, isLoading } = useCMSettings();
  const updateMutation = useUpdateCMSettings();

  if (isLoading) {
    return <Skeleton className="h-24 w-full" />;
  }

  const enabled = data?.autoStartEnabled ?? false;

  return (
    <div>
      <h3 className="text-base font-semibold mb-1">Scheduled auto-start</h3>
      <p className="text-sm text-muted-foreground mb-3">
        Off unless you turn it on: unattended network changes are opted into here, then by
        each change.
      </p>

      <div className="flex items-start justify-between gap-4 rounded-md border border-border-subtle p-3">
        <div className="min-w-0">
          <Label htmlFor="auto-start-toggle" className="font-medium">
            Allow scheduled auto-start
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            When enabled, an approved change that opted into auto-start will begin
            automatically at its scheduled time. Individual changes still choose whether to
            use it.
          </p>
        </div>
        <Switch
          id="auto-start-toggle"
          checked={enabled}
          disabled={updateMutation.isPending}
          onCheckedChange={(checked) => {
            updateMutation.mutate({ autoStartEnabled: checked });
          }}
        />
      </div>

      {updateMutation.isError ? (
        <Alert variant="destructive" className="mt-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to update setting. Please try again.</AlertDescription>
        </Alert>
      ) : null}
      {updateMutation.isSuccess ? (
        <Alert className="mt-3">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>Saved.</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
